import React, { useState, useRef, forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';

// import { IconXCircle } from '@pentabd/icons';

import DatePicker from './date-picker';
import { Button } from '../button';
import { Input } from '../input';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useEventListener from '../../hooks/useEventListener';
import { SELECT_VIEW } from './date-picker/constant';
import { isInvalidDate } from './date-picker/helper';
import { DateInputProps, Position } from './types';

const HEIGHT = 425;

const DATE_FORMATE = {
  DEFAULT: 'YYYY-MM-DD',
  TIME_PICKER: 'YYYY-MM-DD HH:mm',
  MONTH: 'MM',
  YEAR: 'YYYY',
};

// eslint-disable-next-line react/display-name
const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      name,
      placeholder,
      disabled,
      maximumDate,
      minimumDate,
      range,
      className,
      defaultValue = '',
      onSelectDate,
      portal,
      format = DATE_FORMATE.DEFAULT,
      isTimePicker,
      isMonthPicker,
      isYearPicker,
      isResetValue,
      handleResetValue,
      ...props
    }: DateInputProps,
    ref,
  ) => {
    const [activeSelectView, setActiveSelectView] = useState(SELECT_VIEW.DATE);
    const [isOpen, setIsOpen] = useState(false);
    const [selecting, setSelecting] = useState(false);
    const [newValue, setNewValue] = useState(defaultValue);
    const [date, setDate] = useState(dayjs(defaultValue));
    const [startDate, setStartDate] = useState(
      range
        ? defaultValue.split('--')[0]
          ? dayjs(defaultValue.split('--')[0])
          : null
        : null,
    );
    const [endDate, setEndDate] = useState(
      range
        ? defaultValue.split('--')[1]
          ? dayjs(defaultValue.split('--')[1])
          : null
        : null,
    );
    // const [scroll, setScroll] = useState(0);
    const [dropUp, setDropUp] = useState(false);
    const [position, setPosition] = useState<Position>({});

    const monthDiff = (dateFrom: Dayjs, dateTo: Dayjs) =>
      dateTo.month() -
        dateFrom.month() +
        12 * (dateTo.year() - dateFrom.year()) || 0;

    const [displayDate, setDisplayedDate] = useState(
      range
        ? dayjs().add(
            monthDiff(dayjs(), dayjs(newValue?.split('--')?.[0]) || dayjs()),
            'months',
          )
        : dayjs().add(monthDiff(dayjs(), dayjs(newValue) || dayjs()), 'months'),
    );

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setNewValue(defaultValue);
      setDate(dayjs(defaultValue));

      setStartDate(
        range
          ? defaultValue.split('--')[0]
            ? dayjs(defaultValue.split('--')[0])
            : null
          : null,
      );
      setEndDate(
        range
          ? defaultValue.split('--')[1]
            ? dayjs(defaultValue.split('--')[1])
            : null
          : null,
      );
    }, [defaultValue]);

    const onToggle = () => {
      if (portal) {
        if (wrapperRef.current) {
          setPosition(wrapperRef.current?.getBoundingClientRect());
        }
      }

      const windowHeight = window.innerHeight;
      const instOffsetWithMenu =
        wrapperRef.current.getBoundingClientRect().bottom + HEIGHT;

      setDropUp(instOffsetWithMenu >= windowHeight);
      setIsOpen(!isOpen);
    };

    // const onApply = () => setIsOpen(false);

    // click outside
    const handleClickOutside = () => setIsOpen(false);

    useOnClickOutside(dropdownRef, handleClickOutside);

    // scroll event
    const onScroll = () => {
      if (portal) {
        // setScroll(window.scrollY);
        isOpen && setIsOpen(false);
      }
    };

    useEventListener('scroll', onScroll);

    const onSelect = (dates: {
      date?: Dayjs | null;
      startDate?: Dayjs | null;
      endDate?: Dayjs | null;
      selecting?: boolean | null;
    }) => {
      setDate(dates.date);
      setStartDate(dates?.startDate);
      setEndDate(dates?.endDate);
      setSelecting(dates.selecting);

      if (!range) {
        if (isMonthPicker) {
          const time = dates.date
            ? dates.date.format(DATE_FORMATE.MONTH).toString()
            : '';
          setNewValue(time);
          setDisplayedDate(dates.date);
          onSelectDate && onSelectDate(time);
        } else if (isYearPicker) {
          const time = dates.date
            ? dates.date.format(DATE_FORMATE.YEAR).toString()
            : '';
          setNewValue(time);
          setDisplayedDate(dates.date);
          onSelectDate && onSelectDate(time);
        } else {
          const time = dates.date
            ? dates.date
                .format(isTimePicker ? DATE_FORMATE.TIME_PICKER : format)
                .toString()
            : '';
          setNewValue(time);
          setDisplayedDate(dates.date);
          onSelectDate && onSelectDate(time);
        }
      } else if (dates.endDate) {
        const time =
          dates.startDate
            .format(isTimePicker ? DATE_FORMATE.TIME_PICKER : format)
            .toString() +
          '--' +
          dates.endDate.format(format).toString();
        setNewValue(time);
        onSelectDate && onSelectDate(time);
      } else if (dates.startDate) {
        const time =
          dates.startDate
            .format(isTimePicker ? DATE_FORMATE.TIME_PICKER : format)
            .toString() + '--';
        setNewValue(time);
      } else {
        setNewValue('');
        onSelectDate && onSelectDate('');
      }
    };

    const onClickClear = () => {
      onSelect({
        date: null,
        startDate: null,
        endDate: null,
      });
      setDisplayedDate(
        range
          ? dayjs().add(
              monthDiff(dayjs(), dayjs(newValue?.split('--')?.[0]) || dayjs()),
              'months',
            )
          : dayjs().add(
              monthDiff(dayjs(), dayjs(newValue) || dayjs()),
              'months',
            ),
      );
      setNewValue(defaultValue);
      setIsOpen(false);
      setActiveSelectView(SELECT_VIEW.DATE);
    };

    const Dropdown = () => {
      let top = 0,
        left = 0;

      if (portal) {
        const isDropUp = dropUp
          ? HEIGHT + inputWrapperRef.current.clientHeight + 10
          : 0;

        top = position.y + position.height + window.scrollY - isDropUp;
        left = position.x;
      }

      return (
        <div
          {...(portal && {
            style: {
              top: isNaN(top) ? 0 : top,
              left: isNaN(left) ? 0 : left,
              width: position.width,
            },
          })}
          ref={dropdownRef}
          className="date-input bg-white position-absolute rounded-4 shadow-2xl z-1 mt-2"
        >
          <div className="d-flex flex-column shadow z-1">
            <DatePicker
              date={date}
              startDate={startDate}
              endDate={endDate}
              maxDate={maximumDate}
              minDate={minimumDate}
              onChange={onSelect}
              range={range}
              selecting={selecting}
              displayDate={displayDate}
              setDisplayedDate={setDisplayedDate}
              activeSelectView={activeSelectView}
              setActiveSelectView={setActiveSelectView}
              isTimePicker={isTimePicker}
              isMonthPicker={isMonthPicker}
              isYearPicker={isYearPicker}
              setIsOpen={setIsOpen}
            />
            <div className="d-flex gap-6 border-top p-8">
              <Button
                fill="outline"
                type="danger"
                className="flex-fill"
                onClick={onClickClear}
              >
                Clear
              </Button>
            </div>
          </div>
        </div>
      );
    };

    useEffect(() => {
      if (isResetValue) {
        onClickClear();
        if (handleResetValue) handleResetValue();
      }
    }, [isResetValue]);

    return (
      <div
        ref={wrapperRef}
        className={classNames(className, 'position-relative w-100')}
      >
        <div
          ref={inputWrapperRef}
          className="w-100"
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onClick={!disabled ? onToggle : () => {}}
        >
          <Input
            ref={ref}
            {...props}
            id={name}
            name={name}
            className="w-100 text-left text-decoration-none rounded-4"
            controlling
            value={
              range ? newValue : !isInvalidDate(dayjs(newValue)) ? newValue : ''
            }
            placeholder={placeholder}
            disabled={disabled}
            readOnly
          />
        </div>

        {!portal && isOpen && <Dropdown />}
        {portal && isOpen && createPortal(<Dropdown />, document.body)}
      </div>
    );
  },
);

export default DateInput;
